import { useEffect, useRef } from 'react';
import { AlertTriangle, CheckCircle2, Loader, X } from 'lucide-react';
import { showToast } from '@shared/services/toast';
import { useBulkImportJobStore } from '@shared/store/bulkImportJobStore';
import { useBulkImportStatus } from '../../hooks/useBulkImportStatus';

/**
 * Whether a terminal job ended without finishing its rows.
 *
 * Superseded is not in here: a superseded draft was never started, so it has no result to report.
 * @param {Object|null} status
 */
function hasStoppedEarly(status) {
  return status?.status === 'StoppedOnError' || status?.status === 'Failed';
}

/**
 * Bottom-right readout of the bulk import running for this session.
 *
 * Mounted once in AppLayout, which makes it the single owner of the status poll: it outlives the
 * drawer and survives route changes, so the import keeps being watched wherever the user goes.
 * Polling from the drawer as well would double the request rate into a rate-limited endpoint.
 *
 * Deliberately a readout and nothing more — the counts, the failed-row detail and the "download
 * remaining rows" action stay in the drawer rather than being rebuilt in a corner of the screen.
 */
export default function BulkImportStatusIndicator() {
  const jobId = useBulkImportJobStore((state) => state.jobId);
  const status = useBulkImportJobStore((state) => state.status);
  const isDismissed = useBulkImportJobStore((state) => state.isDismissed);
  const isDrawerOpen = useBulkImportJobStore((state) => state.isDrawerOpen);
  const dismiss = useBulkImportJobStore((state) => state.dismiss);

  useBulkImportStatus(jobId);

  // The toast is the signal for a user who has moved on to another page, so it is fired from here
  // rather than the drawer. Keyed by job id: a re-render must not repeat it, but a second import
  // in the same session must still get its own.
  const announcedJobIdRef = useRef(null);

  useEffect(() => {
    if (!jobId || !status?.isTerminal || announcedJobIdRef.current === jobId) return;

    announcedJobIdRef.current = jobId;

    const created = status.successRows ?? 0;
    const total = status.totalRows ?? 0;

    if (hasStoppedEarly(status)) {
      showToast.warning(
        `Import stopped. ${created} of ${total} users were created — open Bulk import to download the remaining rows.`
      );
      return;
    }

    showToast.success(`Import complete. ${created} of ${total} users created.`);
  }, [jobId, status]);

  // Nothing to say, or the drawer is already saying it in full.
  if (!jobId || !status || isDismissed || isDrawerOpen) return null;

  const total = status.totalRows ?? 0;
  const processed = status.processedRows ?? 0;
  const created = status.successRows ?? 0;
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

  // A job can sit queued behind another company's run. Without saying so it renders as "0%" and is
  // indistinguishable from one that has hung — same reasoning as BulkImportProgress.
  const isQueued = status.status === 'Queued';
  const stoppedEarly = hasStoppedEarly(status);

  let toneClassName = 'border-border bg-background';
  if (status.isTerminal) {
    toneClassName = stoppedEarly ? 'border-warning-line bg-warning-bg' : 'border-success-fg/30 bg-success-bg';
  }

  let icon = <Loader className="h-4 w-4 animate-spin text-foreground" aria-hidden="true" />;
  if (status.isTerminal) {
    icon = stoppedEarly ? (
      <AlertTriangle className="h-4 w-4 text-warning-fg" aria-hidden="true" />
    ) : (
      <CheckCircle2 className="h-4 w-4 text-success-fg" aria-hidden="true" />
    );
  }

  let label = `Importing users… ${percent}%`;
  if (isQueued) {
    label = 'Waiting to start…';
  } else if (status.isTerminal) {
    label = stoppedEarly
      ? `Import stopped — ${created} of ${total} created`
      : `Import complete — ${created} of ${total} created`;
  }

  return (
    <output
      // Top-right belongs to the sonner Toaster, so the indicator takes the free corner. z-50 is
      // the app's top layer, matching the sidebar drawer and Radix overlays.
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-xl border px-4 py-3 shadow-lg ${toneClassName}`}
      aria-live="polite"
      data-testid="bulk-import-status-indicator"
    >
      {icon}
      <p className="text-sm font-medium text-foreground">{label}</p>

      {/* Only offered once the job is terminal: dismissing a running import would leave the user
          with no way to get the progress back short of reopening the drawer. */}
      {status.isTerminal && (
        <button
          type="button"
          onClick={dismiss}
          className="rounded-md p-1 text-muted-foreground hover:bg-surface-hover hover:text-foreground"
          aria-label="Dismiss import status"
          data-testid="bulk-import-status-indicator-dismiss"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </output>
  );
}
